import { useLocation, useNavigate } from "react-router-dom";
import { useSelector } from "react-redux";
import { useTranslation } from "react-i18next";
import { Loader2 } from "lucide-react";
import { Button } from "components/ui/button";
import routes from "routes/routes";
import { RootState } from "../../../redux/store";
import { useAppDispatch } from "../../../redux/useDispatch";
import { accountApiSlice, useLogoutMutation } from "api/account/accountApiSlice";
import { clearAccount } from "../../../redux/slice/account/accountSlice";
import { clearAccountSessionToken } from "authentication/accountSession";
import { clearBookingPersonalData } from "util/clearBookingPersonalData";
import AccountPageLayout from "../components/AccountPageLayout";
import ProfileEditSection from "../components/ProfileEditSection";
import EmailChangeSection from "../components/EmailChangeSection";
import DeleteAccountSection from "../components/DeleteAccountSection";
import DataExportSection from "../components/DataExportSection";
import MembershipsSection from "../components/MembershipsSection";
import OrganizationAdminSection from "../components/OrganizationAdminSection";
import OrganizationPlanSection from "../components/OrganizationPlanSection";
import PasskeyEnrollPrompt from "../components/PasskeyEnrollPrompt";
import { readInvitedOrganizationId } from "../common/invite";

/**
 * H-3a account page — F2 scope: editable profile, OTP-verified email change,
 * bookings link, log out and GDPR account deletion.
 * Rendered behind RequireAccount (see PageRoutes), so a profile is present.
 */
const AccountPage = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const dispatch = useAppDispatch();
  const profile = useSelector((state: RootState) => state.account.profile);
  const [logout, { isLoading: isLoggingOut }] = useLogoutMutation();

  // Invite landing marker, matched against what the backend actually granted.
  const invitedOrganizationId = readInvitedOrganizationId(location.search);
  const invitedMembership = profile?.memberships.find(
    (membership) => membership.organizationId === invitedOrganizationId
  );

  if (!profile) {
    // RequireAccount redirects before this renders; keep a safe fallback.
    return null;
  }

  const handleLogout = async () => {
    try {
      await logout().unwrap();
    } catch {
      // Best-effort: the server-side session may already be gone; the local
      // session is cleared regardless.
    }
    clearAccountSessionToken();
    dispatch(clearAccount());
    // Drop the cached GET /me result so the next login never sees the
    // previous user's profile.
    dispatch(accountApiSlice.util.resetApiState());
    // Session end: the booking form must not keep this user's PII
    // (redux + IDB draft) for the next user of the device (#1873 scenario 4).
    clearBookingPersonalData(dispatch);
    navigate(routes.home);
  };

  return (
    <AccountPageLayout title={t("account.profile.title")}>
      {/* Invite landing (#1902): someone who arrived from an invite mail came
          because of one specific organization — say so by name instead of
          leaving them to spot it in the memberships list. Only rendered when
          the membership actually resolved, so it can never claim a
          membership the backend did not grant. */}
      {invitedMembership && (
        <div className="rounded-md bg-primary/10 px-4 py-3 text-sm text-foreground">
          {t("account.invite.joined", {
            organization: invitedMembership.organizationName,
          })}
        </div>
      )}

      <PasskeyEnrollPrompt />

      <ProfileEditSection key={profile.id} profile={profile} />

      <hr className="border-foreground/10" />

      <EmailChangeSection profile={profile} />

      <hr className="border-foreground/10" />

      <MembershipsSection />

      {/* Renders only for an approved `owner` membership, which for almost
          everyone means never (#2163). */}
      <OrganizationAdminSection />
      <OrganizationPlanSection />

      <Button
        size="lg"
        variant="outline"
        className="w-full"
        disabled={isLoggingOut}
        onClick={handleLogout}
      >
        {isLoggingOut ? (
          <>
            <Loader2 className="mr-2 h-5 w-5 animate-spin" />
            {t("account.profile.logging_out")}
          </>
        ) : (
          t("account.profile.logout")
        )}
      </Button>

      <hr className="border-foreground/10" />

      <DataExportSection />

      <hr className="border-foreground/10" />

      <DeleteAccountSection />
    </AccountPageLayout>
  );
};

export default AccountPage;
